<template>
  <section>
    <div class="columns">
      <div class="column">
        <div class="container mt-3">
          <div class="box">
            <!-- Título Hero -->
            <section class="hero is-primary welcome is-small mb-4">
              <div class="hero-body">
                <div class="container">
                  <h1 class="title">
                    <i class="fas fa-clipboard-check"></i>
                    Bandeja de Precalificación de la Comisión
                  </h1>
                  <p class="subtitle" v-if="isCommitteeMember && selectedPerformances.length > 0">
                    {{ selectedPerformances.length }} declaración(es) seleccionada(s)
                  </p>
                </div>
              </div>
            </section>

            <!-- Filtros -->
            <div class="box">
              <div class="columns">
                <!-- Selector de Departamento (solo para Secretaría Académica) -->
                <div class="column is-3" v-if="isSecretariaAcademica">
                  <b-field label="Departamento:">
                    <b-select v-model="filters.department_id" @input="onDepartmentChange" expanded placeholder="Seleccione un departamento">
                      <option :value="null">-- Seleccione un departamento --</option>
                      <option v-for="dept in departments" :key="dept.unit_id" :value="dept.unit_id">
                        {{ dept.name }}
                      </option>
                    </b-select>
                  </b-field>
                </div>
                
                <div class="column is-3" :class="{ 'is-2': isSecretariaAcademica }">
                  <b-field label="Año:">
                    <b-select v-model="filters.year" @input="onYearChange" expanded>
                      <option v-for="yearInfo in availableYears" :key="yearInfo.year" :value="yearInfo.year">
                        {{ yearInfo.year }}
                        <span v-if="yearInfo.has_active_phase"> ✓ (Activo)</span>
                        <span v-else-if="yearInfo.is_current"> (Actual)</span>
                        <span v-else> (Histórico)</span>
                      </option>
                    </b-select>
                  </b-field>
                  <p v-if="selectedYearInfo" class="help">
                    <b-icon :icon="selectedYearInfo.has_active_phase ? 'check-circle' : 'history'" size="is-small"></b-icon>
                    <span v-if="selectedYearInfo.has_active_phase" class="has-text-success">Proceso activo</span>
                    <span v-else class="has-text-grey">Histórico</span>
                  </p>
                </div>
                
                <div class="column" :class="isSecretariaAcademica ? 'is-4' : 'is-5'">
                  <b-field label="Académico:">
                    <b-input 
                      v-model="filters.academic_name" 
                      @input="filterByName"
                      placeholder="Buscar por nombre..."
                      icon="search"
                    ></b-input>
                  </b-field>
                </div>

                <div class="column is-2 is-flex is-align-items-flex-end">
                  <b-button 
                    @click="resetFilters" 
                    type="is-light"
                    icon-left="redo"
                    expanded
                    class="mb-2"
                  >
                    Limpiar Filtros
                  </b-button>
                </div>
              </div>
              
              <!-- Botón de firma masiva y descarga de resumen -->
              <div class="columns">
                <div class="column" v-if="isCommitteeMember && filteredPerformances.length > 0">
                  <b-button
                    @click="openBulkSignModal"
                    type="is-success"
                    icon-left="signature"
                    :disabled="selectedPerformances.length === 0 || bulkSigning"
                    :loading="bulkSigning"
                    expanded
                  >
                    <span v-if="selectedPerformances.length === 0">Seleccione declaraciones para firmar</span>
                    <span v-else>Firmar {{ selectedPerformances.length }} Seleccionada(s)</span>
                  </b-button>
                </div>
                <div class="column">
                  <b-button
                    @click="previewSummaryPDF"
                    type="is-info"
                    icon-left="file-pdf"
                    :loading="generatingSummaryPdf"
                    :disabled="isSecretariaAcademica && !filters.department_id"
                    expanded
                  >
                    <span v-if="isSecretariaAcademica && !filters.department_id">Seleccione un departamento para ver</span>
                    <span v-else>Ver Cuadro Resumen</span>
                  </b-button>
                </div>
              </div>
            </div>
            
            <!-- Mensaje para Secretaría Académica si no hay departamento seleccionado -->
            <b-message v-if="isSecretariaAcademica && !filters.department_id" type="is-warning" has-icon>
              <strong>Seleccione un departamento</strong>
              <p>Para ver las precalificaciones, debe seleccionar un departamento del filtro superior.</p>
            </b-message>

            <!-- Loading -->
            <div v-if="loading" class="has-text-centered py-6">
              <b-loading :active="loading" :is-full-page="false"></b-loading>
              <p class="mt-4">Cargando desempeños...</p>
            </div>

            <!-- Tabla de Desempeños -->
            <div v-else-if="filteredPerformances.length > 0">
              <b-table
                :data="filteredPerformances"
                :hoverable="true"
                :striped="true"
                :mobile-cards="true"
                :checked-rows.sync="selectedPerformances"
                :is-row-checkable="(row) => isCommitteeMember && canSignPerformance(row)"
                checkable
                checkbox-position="left"
              >
                <b-table-column field="academic_name" label="Académico" v-slot="props">
                  <strong>{{ props.row.academic_name }}</strong>
                </b-table-column>

                <b-table-column field="unit_name" label="Unidad" v-slot="props">
                  {{ props.row.unit_name }}
                </b-table-column>

                <b-table-column field="year" label="Año" centered v-slot="props">
                  <b-tag type="is-info">{{ props.row.year }}</b-tag>
                </b-table-column>

                <b-table-column field="current_state" label="Estado" centered v-slot="props">
                  <b-tag :type="getStateType(props.row.current_state)">{{ props.row.current_state }}</b-tag>
                </b-table-column>

                <b-table-column field="total_activities" label="Total Actividades" centered v-slot="props">
                  <b-tag type="is-light">{{ props.row.total_activities }}</b-tag>
                </b-table-column>

                <b-table-column field="scored_activities" label="Calificadas" centered v-slot="props">
                  <b-tag :type="props.row.scored_activities > 0 ? 'is-success' : 'is-light'">
                    {{ props.row.scored_activities }}
                  </b-tag>
                </b-table-column>

                <b-table-column field="progress_percentage" label="Progreso" v-slot="props">
                  <b-progress
                    :value="props.row.progress_percentage"
                    :type="getProgressType(props.row.progress_percentage)"
                    show-value
                    format="percent"
                  >
                  </b-progress>
                </b-table-column>

                <b-table-column label="Acciones" centered v-slot="props">
                  <a 
                    :href="`/performance_precalifications/${props.row.performance.id}`"
                    class="button is-small is-primary"
                    data-turbolinks="false"
                  >
                    <span class="icon">
                      <i class="fas fa-eye"></i>
                    </span>
                    <span>Ver Detalle</span>
                  </a>
                </b-table-column>
              </b-table>
            </div>

            <!-- No hay resultados -->
            <div v-else>
              <b-message type="is-info" has-icon>
                <strong>No hay desempeños disponibles</strong>
                <p>No se encontraron desempeños en estado de precalificación con los filtros aplicados.</p>
              </b-message>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Firma Masiva -->
    <b-modal v-model="showBulkSignModal" has-modal-card trap-focus :destroy-on-hide="false" aria-role="dialog" aria-modal>
      <div class="modal-card" style="width: auto">
        <header class="modal-card-head">
          <p class="modal-card-title">
            <span class="icon-text">
              <span class="icon has-text-success">
                <i class="fas fa-signature"></i>
              </span>
              <span>Firma Masiva de Precalificaciones</span>
            </span>
          </p>
          <button type="button" class="delete" @click="closeBulkSignModal"></button>
        </header>
        <section class="modal-card-body">
          <div class="content">
            <p><strong>Está a punto de firmar {{ selectedPerformances.length }} declaración(es) de precalificación.</strong></p>
            
            <article class="message is-info">
              <div class="message-body">
                <p><strong>Declaraciones seleccionadas:</strong></p>
                <ul>
                  <li v-for="(perf, index) in selectedPerformances" :key="index">
                    {{ perf.academic_name }} - Año {{ perf.year }}
                  </li>
                </ul>
              </div>
            </article>

            <div v-if="!verificationCodeSent">
              <p class="mb-4">Se enviará un código de verificación a su correo electrónico.</p>
              <b-button
                @click="sendBulkVerificationCode"
                type="is-primary"
                icon-left="envelope"
                :loading="sendingCode"
                expanded
              >
                Enviar Código de Verificación
              </b-button>
            </div>

            <div v-else>
              <b-message type="is-success" has-icon>
                Código de verificación enviado a su correo electrónico
              </b-message>
              
              <b-field label="Ingrese el código de verificación">
                <b-input
                  v-model="verificationCode"
                  placeholder="Ingrese el código de 6 dígitos"
                  maxlength="6"
                  expanded
                  type="text"
                ></b-input>
              </b-field>

              <div class="buttons is-right mt-4">
                <b-button @click="closeBulkSignModal" type="is-light">
                  Cancelar
                </b-button>
                <b-button
                  @click="confirmBulkSign"
                  type="is-success"
                  icon-left="signature"
                  :disabled="verificationCode.length !== 6"
                  :loading="bulkSigning"
                >
                  Confirmar Firma Masiva
                </b-button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </b-modal>

    <!-- Modal de Vista Previa del PDF -->
    <b-modal v-model="showPdfPreviewModal" :width="1200" scroll="keep" :can-cancel="['escape', 'outside']">
      <div class="box" style="height: 90vh;">
        <div class="level mb-3">
          <div class="level-left">
            <div class="level-item">
              <h4 class="title is-4">
                <span class="icon-text">
                  <span class="icon has-text-info">
                    <i class="fas fa-file-pdf"></i>
                  </span>
                  <span>Vista Previa - Cuadro Resumen</span>
                </span>
              </h4>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item">
              <b-button
                @click="downloadCurrentPdf"
                type="is-success"
                icon-left="download"
              >
                Descargar PDF
              </b-button>
            </div>
            <div class="level-item">
              <b-button
                @click="showPdfPreviewModal = false"
                type="is-light"
                icon-left="times"
              >
                Cerrar
              </b-button>
            </div>
          </div>
        </div>
        <iframe
          v-if="pdfPreviewUrl"
          :src="pdfPreviewUrl"
          style="width: 100%; height: calc(100% - 60px); border: 1px solid #ddd; border-radius: 4px;"
        ></iframe>
      </div>
    </b-modal>
  </section>
</template>

<script>
import jsPDF from "jspdf";
import "jspdf-autotable";

export default {
  name: 'PrecalificationDashboard',
  
  data() {
    return {
      performances: [],
      filteredPerformances: [],
      selectedPerformances: [],
      filters: {
        year: null, // Se establecerá dinámicamente
        academic_name: '',
        department_id: null // Filtro de departamento para Secretaría Académica
      },
      loading: false,
      isCommitteeMember: false,
      isSecretariaAcademica: false, // Nuevo: detectar si es Secretaría Académica
      departments: [], // Nuevo: lista de departamentos
      currentUserRole: null,
      showBulkSignModal: false,
      verificationCodeSent: false,
      verificationCode: '',
      sendingCode: false,
      bulkSigning: false,
      generatingSummaryPdf: false,
      showPdfPreviewModal: false,
      pdfPreviewUrl: null,
      currentPdfDoc: null,
      currentPdfFilename: '',
      availableYears: [], // Array de objetos {year, has_active_phase, is_current}
      currentYear: null // Año actual seleccionado por el backend
    }
  },
  
  computed: {
    selectedYearInfo() {
      if (!this.filters.year || !this.availableYears.length) return null;
      return this.availableYears.find(y => y.year === parseInt(this.filters.year));
    }
  },
  
  mounted() {
    console.log('===== PrecalificationDashboard MOUNTED =====');
    this.checkUserRole().then(() => {
      // Cargar departamentos solo después de verificar el rol
      if (this.isSecretariaAcademica) {
        this.loadDepartments()
      }
      this.loadAvailableYears()
      // Solo cargar performances si NO es secretaría académica (ellos deben seleccionar departamento primero)
      if (!this.isSecretariaAcademica) {
        this.loadPerformances()
      }
    })
  },
  
  methods: {
    async loadAvailableYears() {
      console.log('=== LOADING AVAILABLE YEARS ===')
      try {
        const response = await fetch('/performance_precalifications/available_years.json')
        console.log('Response status:', response.status)
        
        if (response.ok) {
          const data = await response.json()
          console.log('Response data:', data)
          
          if (data.success) {
            // Validación defensiva: verificar que years es un array de objetos con la estructura esperada
            if (!Array.isArray(data.years)) {
              console.error('API ERROR: data.years is not an array:', typeof data.years)
              throw new Error('Invalid API response: years must be an array')
            }
            
            if (data.years.length > 0 && typeof data.years[0] !== 'object') {
              console.error('API ERROR: data.years contains invalid items. Expected objects with {year, has_active_phase, is_current}, got:', data.years[0])
              throw new Error('Invalid API response: years must contain year objects')
            }
            
            this.availableYears = data.years || []
            this.currentYear = data.current_year
            
            console.log('Available years set to:', this.availableYears)
            console.log('Current year set to:', this.currentYear)
            
            // Establecer el año actual como filtro por defecto
            if (this.currentYear) {
              this.filters.year = this.currentYear
            } else if (this.availableYears.length > 0) {
              // Fallback al primer año disponible
              this.filters.year = this.availableYears[0].year
            }
            
            console.log('Filter year set to:', this.filters.year)
          } else {
            console.log('Success=false, using fallback')
            // Usuario no es miembro del comité, usar año actual
            const currentYear = new Date().getFullYear()
            this.availableYears = [{ year: currentYear, has_active_phase: true, is_current: true }]
            this.filters.year = currentYear
          }
        }
      } catch (error) {
        console.error('Error loading available years:', error)
        // Fallback: año actual
        const currentYear = new Date().getFullYear()
        this.availableYears = [{ year: currentYear, has_active_phase: false, is_current: true }]
        this.filters.year = currentYear
      }
      console.log('=== END LOADING AVAILABLE YEARS ===')
    },
    
    onYearChange() {
      console.log('Year changed to:', this.filters.year)
      this.loadPerformances()
    },

    async checkUserRole() {
      try {
        const response = await fetch('/performance_precalifications/current_user_role.json')
        if (response.ok) {
          const data = await response.json()
          console.log('Current user role:', data.role)
          this.currentUserRole = data.role
          // Solo los miembros del comité pueden hacer firma masiva
          this.isCommitteeMember = data.role === 'COMISION PRECALIFICADOR'
          // Detectar si es Secretaría Académica
          this.isSecretariaAcademica = data.role === 'SECRETARIA ACADEMICA FING'
        }
      } catch (error) {
        console.error('Error checking user role:', error)
        this.isCommitteeMember = false
        this.isSecretariaAcademica = false
      }
    },
    
    async loadDepartments() {
      try {
        const response = await fetch('/performance_precalifications/departments.json')
        if (response.ok) {
          const data = await response.json()
          console.log('Departments loaded:', data)
          if (data.success) {
            this.departments = data.departamentos || []
          }
        }
      } catch (error) {
        console.error('Error loading departments:', error)
        this.departments = []
      }
    },
    
    onDepartmentChange() {
      console.log('Department changed to:', this.filters.department_id)
      if (this.filters.department_id) {
        this.loadPerformances()
      } else {
        // Limpiar la lista si no hay departamento seleccionado
        this.performances = []
        this.filteredPerformances = []
      }
    },

    async loadPerformances() {
      // Si es secretaría académica y no hay departamento seleccionado, no cargar
      if (this.isSecretariaAcademica && !this.filters.department_id) {
        this.performances = []
        this.filteredPerformances = []
        return
      }
      
      this.loading = true
      this.selectedPerformances = []
      try {
        const params = new URLSearchParams()
        if (this.filters.year) params.append('year', this.filters.year)
        // Añadir filtro de departamento si está seleccionado (para Secretaría Académica)
        if (this.filters.department_id) params.append('unit_id', this.filters.department_id)
        
        const response = await fetch(`/performance_precalifications.json?${params}`)
        if (!response.ok) throw new Error('Error al cargar desempeños')
        
        const data = await response.json()
        this.performances = data
        this.filteredPerformances = data
        
        // Aplicar filtro de nombre si existe
        if (this.filters.academic_name) {
          this.filterByName()
        }
      } catch (error) {
        console.error('Error cargando desempeños:', error)
        this.$buefy.toast.open({
          message: 'Error al cargar los desempeños',
          type: 'is-danger',
          duration: 3000
        })
      } finally {
        this.loading = false
      }
    },
    
    filterByName() {
      const searchTerm = this.filters.academic_name.toLowerCase()
      if (!searchTerm) {
        this.filteredPerformances = this.performances
        return
      }
      
      this.filteredPerformances = this.performances.filter(item => {
        const academicName = item.academic_name.toLowerCase()
        return academicName.includes(searchTerm)
      })
    },
    
    resetFilters() {
      // Usar el primer año disponible o el actual
      const defaultYear = this.availableYears.length > 0 ? this.availableYears[0].year : new Date().getFullYear()
      this.filters.year = defaultYear.toString()
      this.filters.academic_name = ''
      this.filters.department_id = null // Limpiar departamento
      this.selectedPerformances = []
      
      // Si es secretaría académica, limpiar la lista (deben seleccionar departamento)
      if (this.isSecretariaAcademica) {
        this.performances = []
        this.filteredPerformances = []
      } else {
        this.loadPerformances()
      }
    },

    canSignPerformance(performance) {
      // Solo puede firmar si:
      // 1. Está en estado EN PRECALIFICACION
      // 2. Todas las actividades están calificadas (progreso 100%)
      // 3. NO ha firmado todavía (user_already_signed debe ser false)
      return performance.current_state === 'EN PRECALIFICACION' && 
             performance.progress_percentage === 100 &&
             performance.user_already_signed === false
    },

    openBulkSignModal() {
      if (this.selectedPerformances.length === 0) {
        this.$buefy.toast.open({
          message: 'Debe seleccionar al menos una declaración',
          type: 'is-warning',
          duration: 3000
        })
        return
      }
      this.showBulkSignModal = true
    },

    closeBulkSignModal() {
      this.showBulkSignModal = false
      this.verificationCodeSent = false
      this.verificationCode = ''
    },

    async sendBulkVerificationCode() {
      this.sendingCode = true
      try {
        const performanceIds = this.selectedPerformances.map(p => p.performance.id)
        
        const response = await fetch('/performance_precalifications/send_bulk_verification_code', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.getCsrfToken()
          },
          body: JSON.stringify({
            performance_ids: performanceIds
          })
        })

        const data = await response.json()

        if (response.ok && data.success) {
          this.verificationCodeSent = true
          this.$buefy.toast.open({
            message: data.message || 'Código enviado a su correo',
            type: 'is-success',
            duration: 4000
          })
        } else {
          throw new Error(data.error || 'Error al enviar código')
        }
      } catch (error) {
        console.error('Error sending verification code:', error)
        this.$buefy.toast.open({
          message: error.message || 'Error al enviar el código de verificación',
          type: 'is-danger',
          duration: 4000
        })
      } finally {
        this.sendingCode = false
      }
    },

    async confirmBulkSign() {
      this.bulkSigning = true
      try {
        const performanceIds = this.selectedPerformances.map(p => p.performance.id)

        const response = await fetch('/performance_precalifications/bulk_sign', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.getCsrfToken()
          },
          body: JSON.stringify({
            performance_ids: performanceIds,
            verification_code: this.verificationCode
          })
        })

        const data = await response.json()

        if (response.ok && data.success) {
          this.$buefy.toast.open({
            message: `${data.signed_count} declaración(es) firmada(s) exitosamente`,
            type: 'is-success',
            duration: 4000
          })
          
          this.closeBulkSignModal()
          this.loadPerformances()
        } else {
          throw new Error(data.error || 'Error al firmar')
        }
      } catch (error) {
        console.error('Error signing performances:', error)
        this.$buefy.toast.open({
          message: error.message || 'Error al firmar las declaraciones',
          type: 'is-danger',
          duration: 4000
        })
      } finally {
        this.bulkSigning = false
      }
    },

    async previewSummaryPDF() {
      // Validar que secretaría académica tenga departamento seleccionado
      if (this.isSecretariaAcademica && !this.filters.department_id) {
        this.$buefy.toast.open({
          message: 'Debe seleccionar un departamento para generar el resumen',
          type: 'is-warning',
          duration: 3000
        })
        return
      }
      
      this.generatingSummaryPdf = true;
      try {
        // 1. Obtener datos del backend
        const params = new URLSearchParams();
        if (this.filters.year) params.append('year', this.filters.year);
        // Incluir unit_id para Secretaría Académica
        if (this.filters.department_id) params.append('unit_id', this.filters.department_id);
        
        const response = await fetch(`/performance_precalifications/department_summary.json?${params}`);
        if (!response.ok) throw new Error('Error al obtener datos del resumen');
        
        const data = await response.json();
        const performances = data.performances || [];
        const departmentName = data.department_name || "DEPARTAMENTO ACADÉMICO";
        const year = data.year || new Date().getFullYear();
        
        // 2. Configurar PDF
        let doc = new jsPDF({
          orientation: "portrait",
          format: "letter",
        });
        
        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;
        const margin = 25; // mm
        
        // --- HEADER ---
        // Logo Secretaría Académica (arriba a la izquierda)
        let imgPath = "/sec_academica_principal_gris.png";
        let imgWidth = 55; // Tamaño pequeño
        try {
            const imgResponse = await fetch(imgPath);
            const blob = await imgResponse.blob();
            const base64Image = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
            doc.addImage(base64Image, "PNG", margin, 10, imgWidth, 0);
        } catch (e) {
            console.error("Error loading logo", e);
        }
        
        // --- TÍTULO ---
        let currentY = 45;
        doc.setFontSize(12);
        doc.text("CUADRO RESUMEN CONVENIO DE DESEMPEÑO DEPARTAMENTO ACADÉMICO", pageWidth / 2, currentY, { align: "center" });
        
        // --- METADATOS ---
        currentY += 15;
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text(`NOMBRE DEPARTAMENTO ACADÉMICO: ${departmentName}`, margin, currentY);
        
        const today = new Date();
        const dateStr = `${today.getDate().toString().padStart(2, '0')}/${(today.getMonth() + 1).toString().padStart(2, '0')}/${today.getFullYear()}`;
        
        // Imprimir fecha en la siguiente línea para evitar solapamiento
        currentY += 5;
        doc.text(`Fecha: ${dateStr}`, pageWidth - margin, currentY, { align: "right" });
        
        // --- TABLA PRINCIPAL ---
        currentY += 10;
        
        // Preparar filas
        let tableRows = [];
        let totals = {
          docencia: 0,
          investigacion: 0,
          asistencia: 0,
          perfeccionamiento: 0,
          administracion: 0,
          extension: 0,
          educacion_continua: 0,
          final: 0
        };
        let countWithScore = 0;
        
        performances.forEach(p => {
          const scores = p.scores || {};
          const finalScore = p.has_final_score ? parseFloat(p.final_score) : null;
          
          // Formatear valores (si es 0 o null, mostrar vacío o guión)
          const fmt = (val) => (val !== null && val !== undefined && val > 0) ? val.toFixed(2) : "";
          
          tableRows.push([
            p.name,
            fmt(scores.docencia),
            fmt(scores.investigacion),
            fmt(scores.asistencia),
            fmt(scores.perfeccionamiento),
            fmt(scores.administracion),
            fmt(scores.extension),
            fmt(scores.educacion_continua),
            finalScore ? finalScore.toFixed(2) : "Pendiente",
            p.final_score_label || ""
          ]);
          
          // Acumular para promedios (solo si tiene nota final válida para el promedio general)
          if (p.has_final_score) {
            totals.docencia += scores.docencia || 0;
            totals.investigacion += scores.investigacion || 0;
            totals.asistencia += scores.asistencia || 0;
            totals.perfeccionamiento += scores.perfeccionamiento || 0;
            totals.administracion += scores.administracion || 0;
            totals.extension += scores.extension || 0;
            totals.educacion_continua += scores.educacion_continua || 0;
            totals.final += finalScore;
            countWithScore++;
          }
        });
        
        // Fila de Promedio Departamento
        if (countWithScore > 0) {
          const avg = (val) => (val / countWithScore).toFixed(2);
          tableRows.push([
            { content: "Promedio Departamento", styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
            { content: avg(totals.docencia), styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
            { content: avg(totals.investigacion), styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
            { content: avg(totals.asistencia), styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
            { content: avg(totals.perfeccionamiento), styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
            { content: avg(totals.administracion), styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
            { content: avg(totals.extension), styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
            { content: avg(totals.educacion_continua), styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
            { content: avg(totals.final), styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
            { content: "", styles: { fillColor: [240, 240, 240] } }
          ]);
        }
        
        doc.autoTable({
          startY: currentY,
          head: [[
            "Nombre Profesores",
            "D",
            "I+D",
            "AT",
            "P",
            "AA",
            "E",
            "EC",
            "Promedio",
            "Calificación"
          ]],
          body: tableRows,
          theme: 'grid',
          styles: {
            fontSize: 8,
            cellPadding: 2,
            valign: 'middle',
            halign: 'center'
          },
          headStyles: {
            fillColor: [255, 255, 255],
            textColor: [0, 0, 0],
            lineWidth: 0.1,
            lineColor: [0, 0, 0],
            fontStyle: 'bold'
          },
          columnStyles: {
            0: { halign: 'left', cellWidth: 60 }, // Nombre
            9: { cellWidth: 25 } // Calificación
          },
          margin: { left: margin, right: margin }
        });
        
        // --- FIRMAS ---
        currentY = doc.lastAutoTable.finalY + 20;
        
        // Verificar espacio para firmas
        if (currentY + 60 > pageHeight - 30) {
          doc.addPage();
          currentY = 30;
        }
        
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.text("COMISIÓN DE EVALUACIÓN DESEMPEÑO", margin, currentY);
        
        currentY += 10;
        
        const committee = data.committee || {};
        
        // Función helper para formatear la información del miembro
        const formatMemberInfo = (member, number) => {
          if (member && member.name) {
            return {
              name: member.name,
              details: `${member.role} - ${member.position} - ${member.department}`
            };
          }
          return {
            name: '_________________________________________',
            details: ''
          };
        };
        
        // Miembro 1 - Director
        const member1 = formatMemberInfo(committee.director, 1);
        currentY += 12;
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text(`1.- ${member1.name}`, margin, currentY);
        if (member1.details) {
          currentY += 5;
          doc.setFontSize(8);
          doc.setFont("helvetica", "italic");
          doc.text(`     ${member1.details}`, margin, currentY);
        }
        
        // Miembro 2 - Par Interno
        const member2 = formatMemberInfo(committee.member_2, 2);
        currentY += 12;
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text(`2.- ${member2.name}`, margin, currentY);
        if (member2.details) {
          currentY += 5;
          doc.setFontSize(8);
          doc.setFont("helvetica", "italic");
          doc.text(`     ${member2.details}`, margin, currentY);
        }
        
        // Miembro 3 - Par Externo
        const member3 = formatMemberInfo(committee.member_3, 3);
        currentY += 12;
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text(`3.- ${member3.name}`, margin, currentY);
        if (member3.details) {
          currentY += 5;
          doc.setFontSize(8);
          doc.setFont("helvetica", "italic");
          doc.text(`     ${member3.details}`, margin, currentY);
        }
        
        // --- LEYENDA ---
        currentY += 20;
        
        // Verificar espacio para leyenda
        if (currentY + 50 > pageHeight - 30) {
          doc.addPage();
          currentY = 30;
        }
        
        doc.autoTable({
          startY: currentY,
          head: [["CATEGORIAS", "ESCALA DE CALIFICACIÓN(*)", "CONCEPTO"]],
          body: [
            ["D: Docencia", "3,7 a 4,0 : Sobresaliente", "Hizo más de lo comprometido"],
            ["I+D: Investigación y Desarrollo", "2,8 a 3,69: Bueno", "Hizo lo comprometido"],
            ["AT: Asistencia Técnica", "2,0 a 2,79: Aceptable", "Hizo un poco menos que lo comprometido"],
            ["P: Perfeccionamiento", "1,5 a 1,99: Condicional", "Hizo menos del 50% de lo comprometido"],
            ["AA: Administración Académica", "0 a 1,49 : Insuficiente", "No hizo nada"],
            ["E: Extensión", "", "(*) Según 5949 año 2009"],
            ["EC: Educación Continua", "", ""]
          ],
          theme: 'plain',
          styles: {
            fontSize: 7,
            cellPadding: 1
          },
          headStyles: {
            fontStyle: 'bold'
          },
          columnStyles: {
            0: { cellWidth: 60 },
            1: { cellWidth: 50 }
          },
          margin: { left: margin, right: margin }
        });
        
        // --- FOOTER ---
        const footerY = pageHeight - 15;
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.text("FACULTAD DE INGENIERÍA DE LA UNIVERSIDAD DE SANTIAGO DE CHILE", pageWidth / 2, footerY - 6, { align: "center" });
        doc.text("Las Sophoras 165-Estación Central-Santiago-Chile", pageWidth / 2, footerY - 3, { align: "center" });
        doc.text("www.fing.usach.cl", pageWidth / 2, footerY, { align: "center" });
        
        // Guardar referencia al PDF para preview/descarga
        this.currentPdfDoc = doc;
        this.currentPdfFilename = `Cuadro_Resumen_Desempeño_${year}.pdf`;
        
        // Generar URL para vista previa
        this.pdfPreviewUrl = doc.output('bloburl');
        this.showPdfPreviewModal = true;
        
      } catch (error) {
        console.error("Error generating summary PDF:", error);
        this.$buefy.toast.open({
          message: 'Error al generar el documento resumen',
          type: 'is-danger'
        });
      } finally {
        this.generatingSummaryPdf = false;
      }
    },

    downloadCurrentPdf() {
      if (this.currentPdfDoc && this.currentPdfFilename) {
        this.currentPdfDoc.save(this.currentPdfFilename);
        this.$buefy.toast.open({
          message: 'Documento descargado exitosamente',
          type: 'is-success'
        });
      }
    },

    getCsrfToken() {
      return document.querySelector('meta[name="csrf-token"]').getAttribute('content')
    },
    
    getProgressType(percentage) {
      if (percentage === 100) return 'is-success'
      if (percentage >= 50) return 'is-warning'
      return 'is-danger'
    },
    
    getStateType(state) {
      const stateColors = {
        'EN PRECALIFICACION': 'is-warning',
        'PRECALIFICADA': 'is-success',
        'DEVUELTA AL ACADEMICO': 'is-danger',
        'ACEPTADA': 'is-info'
      }
      return stateColors[state] || 'is-light'
    }
  }
}
</script>

<style scoped>
.py-6 {
  padding-top: 3rem;
  padding-bottom: 3rem;
}
</style>
